/**
 * The timing logic that decides whether keystrokes were a scan.
 *
 * Pulled out of the React hook and made pure so it can be tested without
 * a DOM. The hook around it is four lines of addEventListener; this is
 * the part that can be wrong, and it was.
 *
 * A HID scanner presents as a keyboard, so speed is the only signal.
 * Two limits rather than one:
 *
 *  - a per-key gap, which starts a new sequence when exceeded, and
 *  - a total duration for the whole code.
 *
 * The gap alone is not enough. Keystrokes are delivered to JS when the
 * main thread is free, so a render or a network callback can stretch one
 * gap well past a scanner's true inter-key time — a tight per-key limit
 * throws away a real scan whenever the tablet is briefly busy. The total
 * duration is what actually separates a scanner from a person: seven
 * random characters take a scanner well under 200ms and a human over a
 * second, and no amount of main-thread jitter closes that gap.
 */
export const MAX_GAP_MS = 200;
export const MAX_TOTAL_MS = 700;
export const MIN_LENGTH = 4;

export type ScanState = {
  buffer: string;
  firstKeyAt: number;
  lastKeyAt: number;
};

export const emptyScanState: ScanState = {
  buffer: "",
  firstKeyAt: 0,
  lastKeyAt: 0,
};

export type ScanStep = {
  state: ScanState;
  /** Set only when the keystrokes look like a completed scan. */
  code: string | null;
};

/**
 * Feed one keydown. `key` is the KeyboardEvent key value.
 *
 * Returns the next state, and a code when Enter completed a sequence
 * that arrived fast enough and is long enough to be a barcode.
 */
export function feedKey(
  state: ScanState,
  key: string,
  now: number,
): ScanStep {
  const continues =
    state.buffer.length > 0 && now - state.lastKeyAt <= MAX_GAP_MS;

  if (key === "Enter") {
    if (!continues) return { state: emptyScanState, code: null };

    const code = state.buffer;
    const total = now - state.firstKeyAt;
    const isScan = code.length >= MIN_LENGTH && total <= MAX_TOTAL_MS;
    return { state: emptyScanState, code: isScan ? code : null };
  }

  // Modifiers, arrows and function keys are not part of a barcode. A
  // scanner emits printable characters and then Enter.
  if (key.length !== 1) return { state, code: null };

  return {
    state: continues
      ? { ...state, buffer: state.buffer + key, lastKeyAt: now }
      : { buffer: key, firstKeyAt: now, lastKeyAt: now },
    code: null,
  };
}
